import {
  useDispatch,
  useDocumentsCollectionFromProjectId,
  useFileCollectionFromFileId,
  useFilesFromFileCollectionId,
  useProject,
} from '@witmetrics/data-store';
import { useId } from '@/hooks/useId';
import { useAppState } from '@/providers/AppStateProvider';
import { useString } from '@/hooks/useString';
import { useCollectionViewSettings } from '@/hooks/useCollectionViewSettings';
import FileOverlay from '@/overlays/FileOverlay';
import FileCollectionOverlay from '@/overlays/FileCollectionOverlay';
import Section from '@/overlays/CaseOverlay/Section';
import DocumentsHeader, {
  type DocumentsHeaderProps,
} from '@/components/DocumentsHeader';
import FileCollection from '@/components/FileCollection';
import {
  createDocumentsCollection,
  createFileKeypoint,
  filterCollectionItems,
} from './utils';

export type DocumentsSectionProps = {
  isFetchingData?: boolean;
};

export default function DocumentsSection({
  isFetchingData,
}: DocumentsSectionProps) {
  const dispatch = useDispatch();
  const { activeProjectId } = useAppState();
  const fileCollection = useDocumentsCollectionFromProjectId(activeProjectId);
  const project = useProject(activeProjectId);
  const files = useFilesFromFileCollectionId(fileCollection?.id);
  const [activeFileId, setFileId, { clear: clearFileId }] = useId();
  const [search, setSearch] = useString();
  const [collectionView, setCollectionView] = useCollectionViewSettings();
  const activeFileCollection = useFileCollectionFromFileId(activeFileId);

  const handleCreateCollection: DocumentsHeaderProps['onCreateCollection'] =
    async () => {
      // If there's no Documents file collection, need to create one before uploading a document
      if (!project) throw Error('No active case');
      const result = await createDocumentsCollection(project!);
      dispatch.addProjectFileCollections([result]);
      return result.fileCollectionId;
    };

  const handleUploadFile = async (fileId: number) => {
    dispatch.addProjectKeypoints([await createFileKeypoint(project!, fileId)]);
  };

  return (
    <>
      {activeFileId && activeFileCollection && (
        <FileCollectionOverlay
          fileCollectionId={activeFileCollection.id}
          initialFileId={activeFileId}
          onClose={clearFileId}
        />
      )}
      {activeFileId && !activeFileCollection && (
        <FileOverlay fileId={activeFileId} onClose={clearFileId} />
      )}
      <Section>
        <DocumentsHeader
          isFetchingData={isFetchingData}
          fileCollectionId={fileCollection?.id}
          collectionView={collectionView!}
          search={search}
          onSearch={setSearch}
          onChangeView={setCollectionView}
          onCreateCollection={handleCreateCollection}
          onSaveDocument={handleUploadFile}
        />
        <FileCollection
          isFetchingData={isFetchingData}
          parentFileCollectionId={fileCollection?.id}
          variant="aurora"
          collectionView={collectionView!}
          files={filterCollectionItems(files, search)}
          fileCollections={[] /* TODO */}
          onViewFile={setFileId}
          onViewFileCollection={() => null /* TODO */}
          onOpenFileCollection={() => null /* TODO */}
        />
      </Section>
    </>
  );
}
